import React from 'react';
import { DOMAINS_UPDATED, EmailDomain, useEmailDomainsService, VerificationStatus } from 'nekst-api';
import { Cell, List, ListDataRefresher, TextLink, useShowContextRequired } from 'ui-builder';
import TextField from '../../shared/uibuilder/show/field/TextField';
import InnerBlock from '../../shared/web/layout/InnerBlock';
import { CreateEmailDomainButton } from './CreateEmailDomainForm';
import { GridLayout, IconButton, IconDeleteButton, IconType } from 'features/nekst-widgets';
import { useDomainSetUpWalkthroughPopup } from './DomainSetUpWalkthrough';
import VerificationStatusField from './VerificationStatusField';
import useSettingsNavigator from '../settingsNavigator';

import styles from './EmailDomain.module.scss';

function OpenDomainSetupPopupButton() {
  const domainSetUpWalkthroughPopup = useDomainSetUpWalkthroughPopup();

  const showContext = useShowContextRequired<EmailDomain>();

  return (
    <IconButton
      onClick={() => domainSetUpWalkthroughPopup.openPopup(
        showContext.data!,
      )}
      type={IconType.GEAR}
    />
  );
}

function DeleteEmailDomainButton() {
  const showContext = useShowContextRequired<EmailDomain>();

  const emailDomainsService = useEmailDomainsService();

  return (
    <IconDeleteButton
      deleteFunc={async () => {
        await emailDomainsService.deleteTeamDomain(showContext.data!.id);
      }}
      message={`Are you sure you want to delete domain "${showContext.data!.domain}"? This will remove all emails set up for it.`}
    />
  );
}

function RefreshDomainVerificationStatusButton() {
  const showContext = useShowContextRequired<EmailDomain>();

  const emailDomainsService = useEmailDomainsService();

  if (showContext.data!.status === VerificationStatus.VERIFIED) {
    return null;
  } else {
    return (
      <IconButton
        onClick={async () => await emailDomainsService.verifyDomain(showContext.data!.id)}
        type={IconType.REFRESH}
        title="Refresh Verification Status"
      />
    );
  }
}


export default function EmailsDomainList() {
  const emailDomainsService = useEmailDomainsService();
  const settingsNavigator = useSettingsNavigator();

  return (
    <InnerBlock title="Email Domain Setup" headerRight={<CreateEmailDomainButton />}>
      <List getDataFunc={emailDomainsService.getTeamDomains}>
        <GridLayout
          weights={[10, 2]}
          noDataMessage="Email Domains are not set up yet."
        >
          <TextField source="domain" label="Domain" />
          <Cell label="Verification Status">
            <VerificationStatusField source="status" label="Verification Status" />
            <RefreshDomainVerificationStatusButton />
          </Cell>
          <Cell label="Actions">
            <OpenDomainSetupPopupButton />
            <DeleteEmailDomainButton />
          </Cell>
        </GridLayout>
        <ListDataRefresher eventName={DOMAINS_UPDATED} />
      </List>
      <div className={styles.hint}>
        If your domain is in "Pending" status, it's currently being verified. This should take no longer than 24 hours (usually, just a few minutes) and
        you can manually refresh the status at any time. Once your domain is verified, you can setup you Email Address <TextLink onClick={settingsNavigator.openSendingEmailPage}>here</TextLink>.
      </div>
    </InnerBlock>
  );
}
